// Shared WebMCP (Web Model Context Protocol) primitives.
//
// WebMCP lets an MCP-capable browser/agent discover and invoke page
// capabilities. The W3C proposal surfaces the registry at
// `navigator.modelContext`. We feature-detect it so there is zero effect for
// ordinary human visitors whose browsers don't expose it.
//
// Tool descriptors use a JSON-Schema `inputSchema` + async `execute` returning
// `{ content: [{ type: "text", text }], structuredContent? }` (the WebMCP / MCP
// tool-result contract) so the schema passes the browser's WebMCP diagnostics
// ("tools registered" / "schemas are valid").

export type McpToolResult = {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
  structuredContent?: unknown;
};

export type McpToolDescriptor = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<McpToolResult>;
};

type McpRegistration = { unregister?: () => void } | void;

type ModelContext = {
  registerTool?: (tool: McpToolDescriptor) => McpRegistration;
  provideContext?: (ctx: { tools: McpToolDescriptor[] }) => void;
};

export function getModelContext(): ModelContext | null {
  if (typeof navigator === "undefined") return null;
  const mc = (navigator as unknown as { modelContext?: ModelContext }).modelContext;
  return mc && (typeof mc.registerTool === "function" || typeof mc.provideContext === "function")
    ? mc
    : null;
}

export function errorResult(text: string): McpToolResult {
  return { content: [{ type: "text", text }], isError: true };
}

// Guards against duplicate registrations of the same tool "kind". Multiple
// widgets can mount the same tool (e.g. the homepage cabin widget and the
// listing-page widget, or React StrictMode's double-invoke) — only the first to
// claim a given tool name wins, so the agent never sees duplicate tools or the
// browser's "duplicate registration" diagnostics warning.
const activeToolNames = new Set<string>();

/**
 * Register a single WebMCP tool with the browser's model-context registry.
 * Prefers `registerTool` (returns an unregister handle); falls back to the
 * declarative `provideContext` for implementations that only expose that.
 * Returns a cleanup function, or `undefined` when WebMCP isn't available (in
 * which case this is a pure no-op for human visitors). If a tool of the same
 * name is already active, registration is skipped and a no-op cleanup returned.
 */
export function registerMcpTool(tool: McpToolDescriptor): (() => void) | void {
  const mc = getModelContext();
  if (!mc) return;

  if (activeToolNames.has(tool.name)) {
    // Another widget of the same kind already owns this tool name.
    return () => {};
  }
  activeToolNames.add(tool.name);

  let teardown: () => void = () => {};
  if (typeof mc.registerTool === "function") {
    const reg = mc.registerTool(tool);
    teardown = () => {
      try {
        reg?.unregister?.();
      } catch {
        /* no-op */
      }
    };
  } else if (typeof mc.provideContext === "function") {
    mc.provideContext({ tools: [tool] });
    teardown = () => {
      try {
        mc.provideContext?.({ tools: [] });
      } catch {
        /* no-op */
      }
    };
  }

  return () => {
    activeToolNames.delete(tool.name);
    teardown();
  };
}

export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
